import React, { useState } from 'react';
import axios from 'axios';
import { useAppDispatch, useAppSelector } from 'src/hooks';
import { RedesignButton } from '@components/RedesignButton';
import { Text } from '@components/Text';
import { Input } from '@components/Input';
import { closeCurrentModalAction } from 'src/redesign/features/Modals/store/reducer';
import { accountSelector } from 'src/redesign/features/Account/store/selectors';
import S from './CancelTransferModal.module.scss';
import { showNotification } from '../../Toast/store/actions';
import { API_URL } from 'src/utils/transport';

interface ContactHostModalProps {
  ticketInfo: any;
}

export const ContactHostModal: React.FC<ContactHostModalProps> = ({ ticketInfo }) => {
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useAppDispatch();
  const account = useAppSelector(accountSelector);

  const handleSubmit = async () => {
    if (!message.trim()) return;

    setIsSubmitting(true);
    try {
      await axios.post(
        `${API_URL}/api/v1/event-page/contact-host`,
        {
          site_id: ticketInfo.site_id,
          message: message.trim(),
          subject: 'Question about event',
        },
        {
          withCredentials: true,
        },
      );

      dispatch(
        showNotification({
          severity: 'success',
          detail: 'Your message has been sent to the host successfully.',
          life: 5000,
          position: 'bottom-center',
        }),
      );
      dispatch(closeCurrentModalAction());
    } catch (error) {
      console.error('Failed to send message:', error);
      dispatch(
        showNotification({
          severity: 'error',
          detail: error?.response?.data?.error ?? 'Failed to send message. Please try again.',
          life: 5000,
          position: 'bottom-center',
        }),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const accountEmail = account?.email || 'your registered email';

  return (
    <div className={S.modalContent}>
      <Text variation="h4" bold className={S.title}>
        Contact the Host
      </Text>
      <Text variation="body" color="black-400" className={S.description}>
        Have a question or need assistance with the event? Send a message directly to the host and
        they'll get back to you promptly.
      </Text>

      <div className={S.formField}>
        <Text variation="body-small" bold className={S.fieldLabel}>
          Message *
        </Text>
        <Input
          value={message}
          textAreaProps={{
            onChange: (e) => setMessage(e.target.value),
            rows: 4,
          }}
          placeholder="What's your question about the event?"
          as="textarea"
        />
        <Text variation="body-small" color="black-400" className="mt-2">
          The host will send replies to {accountEmail}.
        </Text>
      </div>

      <div className={S.modalActions}>
        <RedesignButton
          variation="secondary"
          height={52}
          onClick={() => dispatch(closeCurrentModalAction())}
          disabled={isSubmitting}
        >
          <Text regular color="brand-400">
            Cancel
          </Text>
        </RedesignButton>

        <RedesignButton
          variation="primary"
          height={52}
          onClick={handleSubmit}
          disabled={!message.trim() || isSubmitting}
        >
          <Text regular color="white-100">
            {isSubmitting ? 'Sending...' : 'Send Message'}
          </Text>
        </RedesignButton>
      </div>
    </div>
  );
};
